import { useEffect } from 'react'

/**
 * SEO 메타데이터 설정 인터페이스
 * route-definitions.tsx와 SEO 컴포넌트에서 공유
 */
export interface SEOConfig {
  title?: string
  description?: string
  noindex?: boolean // true면 검색엔진 인덱싱 차단
  structuredData?: Record<string, unknown> // JSON-LD structured data
}

/**
 * SEO 메타 태그를 관리하는 훅
 * React 19와 호환되며 외부 의존성이 없음
 */
export function useSEO({ title, description, noindex = false, structuredData }: SEOConfig) {
  useEffect(() => {
    const siteTitle = 'CoCo - コスメインフルエンサーマーケティング'
    // Title에 이미 구분자(| 또는 ｜)가 포함되어 있으면 완전한 타이틀로 간주
    const hasDelimiter = title && (title.includes('|') || title.includes('｜'))
    const fullTitle = title ? (hasDelimiter ? title : `${title} | ${siteTitle}`) : siteTitle

    // Title 설정
    document.title = fullTitle

    // Description meta 태그 설정
    let descriptionMeta = document.querySelector('meta[name="description"]')
    if (description) {
      if (!descriptionMeta) {
        descriptionMeta = document.createElement('meta')
        descriptionMeta.setAttribute('name', 'description')
        document.head.appendChild(descriptionMeta)
      }
      descriptionMeta.setAttribute('content', description)
    } else if (descriptionMeta) {
      descriptionMeta.remove()
    }

    // Robots meta 태그 설정
    let robotsMeta = document.querySelector('meta[name="robots"]')
    if (noindex) {
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta')
        robotsMeta.setAttribute('name', 'robots')
        document.head.appendChild(robotsMeta)
      }
      robotsMeta.setAttribute('content', 'noindex, nofollow')
    } else if (robotsMeta) {
      robotsMeta.remove()
    }

    // Structured Data (JSON-LD) 설정
    const SCRIPT_ID = 'seo-structured-data'
    let structuredDataScript = document.getElementById(SCRIPT_ID)

    if (structuredData) {
      if (!structuredDataScript) {
        structuredDataScript = document.createElement('script')
        structuredDataScript.id = SCRIPT_ID
        structuredDataScript.type = 'application/ld+json'
        document.head.appendChild(structuredDataScript)
      }
      structuredDataScript.textContent = JSON.stringify(structuredData)
    } else if (structuredDataScript) {
      structuredDataScript.remove()
    }

    // Cleanup: 컴포넌트 언마운트 시 제거
    return () => {
      // Title은 다음 페이지에서 덮어쓰므로 제거하지 않음
      // Description, Robots, Structured Data만 제거
      const desc = document.querySelector('meta[name="description"]')
      const robots = document.querySelector('meta[name="robots"]')
      const script = document.getElementById(SCRIPT_ID)
      if (desc && !description) desc.remove()
      if (robots && !noindex) robots.remove()
      if (script && !structuredData) script.remove()
    }
  }, [title, description, noindex, structuredData])
}
